import { DeleteIcon } from '@chakra-ui/icons'
import { Box, Flex, Link, Text as ChakraText } from '@chakra-ui/layout'
import {
  Button,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  useDisclosure,
} from '@chakra-ui/react'
import type { FormikProps } from 'formik'
import React, { useContext } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { BsInfoCircleFill } from 'react-icons/bs'
import { colors } from '../../../ui-kit'
import { PermissionsEnum } from '../../../utils'
import type { MediaLibrarySettings, SettingsResponse } from '../../interfaces'
import { CustomMetadataContext } from '../../providers/CustomMetadataProvider'
import PermissionGuard from '../PermissionGuard'
import CustomMetadataSettings from './CustomMetadataSettings'
import EmptyMLModal from './EmptyMLModal'
import MetaDataFieldsMlSettings from '../MediaLibrary/NewButton/MetaDataFieldsMlSettings'
import { GrDocumentUpload } from 'react-icons/gr'
import { BsFillXDiamondFill as BsFillXDiamondFillIcon } from 'react-icons/bs'

export const MediaLibrarySettingsForm = ({
  form,
}: {
  form: FormikProps<MediaLibrarySettings> | FormikProps<SettingsResponse>
}): React.ReactElement => {
  const { t } = useTranslation()
  const { cmFields } = useContext(CustomMetadataContext)

  const updatedCmField = React.useMemo(() => {
    return cmFields?.map(data => ({
      ...data,
      name: `IK_TEMP_CUSTOM_METADATA_${data.name}`,
      originalName: data.name,
    }))
  }, [cmFields])


  return (
    <PermissionGuard permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}>
      {(hasPermission: boolean) => (
        <AccordionItem borderTop="none">
          <AccordionButton px={12} py={8}>
            <Flex
              flex="1"
              textAlign="left"
              lineHeight="1.5em"
              fontWeight="700"
              style={{ gap: '1em' }}
            >
              <GrDocumentUpload fontSize="1.5em" />
              {t('default_upload_parameters', 'Default upload parameters')}
            </Flex>
            <Box
              background="appPaleGreyThree"
              borderRadius="50%"
              padding="0.25em"
            >
              <AccordionIcon color="appSlate" display="block" />
            </Box>
          </AccordionButton>

          <AccordionPanel px={12}>
            <Box>
              <ChakraText color={colors.appBattleshipGrey}>
                {t(
                  'default_parameters_info',
                  'Add default parameters to be applied when uploading a new file. This does not effect existing files, and can be overridden for new uploads.',
                )}
                &nbsp;
                <Link href="https://docs.imagekit.io/api-reference/upload-file-api/server-side-file-upload">
                  {t('read_more')}
                </Link>
              </ChakraText>
              <MetaDataFieldsMlSettings
                values={form?.values}
                handleChange={form.handleChange}
                form={form}
                cmFields={updatedCmField}
                isDisabled={!hasPermission}
              />
            </Box>
          </AccordionPanel>
        </AccordionItem>
      )}
    </PermissionGuard>
  )
}

export const CustomMetadataSettingComponent = () => {
  const { t } = useTranslation()

  return (
    <AccordionItem>
      <AccordionButton px={12} py={8}>
        <Flex
          flex="1"
          textAlign="left"
          lineHeight="1.5em"
          fontWeight="700"
          style={{ gap: '1em' }}
        >
          <BsFillXDiamondFillIcon fontSize="1.5em" />
          {t('custom_metadata', 'Custom Metadata')}
        </Flex>
        <Box background="appPaleGreyThree" borderRadius="50%" padding="0.25em">
          <AccordionIcon color="appSlate" display="block" />
        </Box>
      </AccordionButton>

      <AccordionPanel px={12}>
        <PermissionGuard permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}>
          {(hasPermission: boolean) =>
            hasPermission ? <CustomMetadataSettings /> : <></>
          }
        </PermissionGuard>
      </AccordionPanel>
    </AccordionItem>
  )
}

export const DeleteMediaLibrary = () => {
  const { t } = useTranslation()
  const { isOpen, onOpen, onClose } = useDisclosure()
  return (
    <PermissionGuard permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}>
      {hasImageSettingPermission =>
        hasImageSettingPermission ? (
          <AccordionItem>
            <AccordionButton px={12} py={8}>
              <Flex
                flex="1"
                textAlign="left"
                lineHeight="1.5em"
                fontWeight="700"
                style={{ gap: '1em' }}
              >
                <DeleteIcon fontSize="1.5em" />
                {t('delete_all_media_library_content')}
              </Flex>
              <Box
                background="appPaleGreyThree"
                borderRadius="50%"
                padding="0.25em"
              >
                <AccordionIcon color="appSlate" display="block" />
              </Box>
            </AccordionButton>

            <AccordionPanel px={12}>
              <Box marginTop="1rem">
                <ChakraText color={colors.appBattleshipGrey}>
                  <Trans
                    t={t}
                    i18nKey="delete_all_media_library_content_description"
                  >
                    This will delete all files and folders from media library.{' '}
                    <b>This action is irreversible.</b>
                  </Trans>
                </ChakraText>
                <Button
                  marginTop="10px"
                  leftIcon={<DeleteIcon />}
                  variant="deleteCTA"
                  onClick={onOpen}
                >
                  {t('empty_media_library', 'Empty media library')}
                </Button>
                <EmptyMLModal isOpen={isOpen} onClose={onClose} />
              </Box>
            </AccordionPanel>
          </AccordionItem>
        ) : (
          <></>
        )
      }
    </PermissionGuard>
  )
}
